import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, CircularProgress, Alert, Grid, Card,
  CardContent, IconButton, Tooltip, FormControl, InputLabel, Select,
  MenuItem, Chip, Button, Collapse, Divider, LinearProgress
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Memory as SolverIcon,
  CheckCircle as SuccessIcon,
  Schedule as PendingIcon,
  Compare as CompareIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '@vortx/shared-ui';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';
import { API_BASE_URL } from '../../services/api';

const ENGINE_COLORS = {
  vortx_flux: { bg: vortxColors.teal, label: 'Vortx Flux' },
  nrel_reopt: { bg: '#f59e0b', label: 'NREL REopt' }
};

// Solver Comparison Component
function SolverComparisonCard() {
  const { currentUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentComparisons, setRecentComparisons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [triggering, setTriggering] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [displayedMessage, setDisplayedMessage] = useState('');

  const fetchStats = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      const token = await currentUser.getIdToken();

      // Fetch stats
      const statsRes = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects/admin/solver-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }

      // Fetch recent comparisons
      const comparisonsRes = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects/admin/solver-comparisons?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (comparisonsRes.ok) {
        const data = await comparisonsRes.json();
        setRecentComparisons(data.comparisons || []);
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching solver stats:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const handleDelete = async (comparisonId) => {
    if (!currentUser || !window.confirm('Delete this comparison run? This cannot be undone.')) return;
    setDeleting(comparisonId);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects/admin/solver-comparisons/${comparisonId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const deletedComp = recentComparisons.find(c => c.id === comparisonId);
        setRecentComparisons(prev => prev.filter(c => c.id !== comparisonId));

        if (stats && deletedComp) {
          const newTotal = Math.max(0, (stats.total_comparisons || 0) - 1);
          const statusKey = deletedComp.overall_status === 'pass' ? 'total_passes' :
                           deletedComp.overall_status === 'fail' ? 'total_failures' :
                           deletedComp.overall_status === 'warning' ? 'total_warnings' :
                           deletedComp.overall_status === 'nrel_error' ? 'total_nrel_errors' : null;

          setStats(prev => ({
            ...prev,
            total_comparisons: newTotal,
            ...(statusKey && { [statusKey]: Math.max(0, (prev[statusKey] || 0) - 1) }),
            pass_rate: newTotal > 0
              ? Math.round(((statusKey === 'total_passes' ? Math.max(0, (prev.total_passes || 0) - 1) : (prev.total_passes || 0)) / newTotal) * 1000) / 10
              : 0
          }));
        }

        if (expandedId === comparisonId) {
          setExpandedId(null);
        }
      } else {
        const data = await res.json();
        alert(data.error || 'Failed to delete comparison');
      }
    } catch (err) {
      console.error('Error deleting comparison:', err);
      alert('Failed to delete comparison');
    } finally {
      setDeleting(null);
    }
  };

  const toggleExpanded = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const handleTriggerComparison = async () => {
    if (!currentUser) return;
    setTriggering(true);
    setStatusMessage('');
    setDisplayedMessage('');
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects/admin/solver-comparisons/trigger`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();
      if (res.ok) {
        const jobId = data.job_id?.slice(0, 8) || 'unknown';

        const statusUpdates = [
          `Verification run started (Job: ${jobId}...)`,
          `Running Vortx Flux optimization...`,
          `Optimization in progress, waiting for solver...`,
          `Submitting to NREL REopt for comparison...`,
          `Waiting for NREL results...`,
          `Comparison complete! Refreshing results...`
        ];

        let updateIndex = 0;
        setStatusMessage(statusUpdates[0]);

        const progressTimer = setInterval(() => {
          updateIndex++;
          if (updateIndex < statusUpdates.length) {
            setStatusMessage(statusUpdates[updateIndex]);
          } else {
            clearInterval(progressTimer);
            fetchStats();
            setTimeout(() => {
              setStatusMessage('');
              setDisplayedMessage('');
            }, 2000);
          }
        }, 30000);

        return () => clearInterval(progressTimer);
      } else {
        setStatusMessage(`Error: ${data.error || 'Failed to trigger verification'}`);
      }
    } catch (err) {
      console.error('Error triggering comparison:', err);
      setStatusMessage('Error: Failed to trigger comparison');
    } finally {
      setTriggering(false);
    }
  };

  // Typewriter effect for status message
  useEffect(() => {
    if (!statusMessage) {
      setDisplayedMessage('');
      return;
    }
    let index = 0;
    setDisplayedMessage('');
    const timer = setInterval(() => {
      if (index < statusMessage.length) {
        setDisplayedMessage(statusMessage.slice(0, index + 1));
        index++;
      } else {
        clearInterval(timer);
      }
    }, 25);
    return () => clearInterval(timer);
  }, [statusMessage]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const getStatusChip = (status) => {
    const statusConfig = {
      pass: { color: 'success', label: 'Pass', icon: null },
      fail: { color: 'error', label: 'Fail', icon: <ErrorIcon fontSize="small" /> },
      warning: { color: 'warning', label: 'Warning', icon: <WarningIcon fontSize="small" /> },
      nrel_error: { color: 'default', label: 'NREL Error', icon: null }
    };
    const config = statusConfig[status] || { color: 'default', label: status };
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        icon={config.icon}
      />
    );
  };

  if (loading) {
    return (
      <Card sx={{ bgcolor: vortxColors.charcoal, mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CompareIcon sx={{ color: vortxColors.gold }} />
            <Typography variant="h6" sx={{ color: vortxColors.white }}>
              Solver Verification
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: vortxColors.gold }} size={32} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ bgcolor: vortxColors.charcoal, mb: 4 }}>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  const passRate = stats?.pass_rate || 0;

  return (
    <Card sx={{ bgcolor: vortxColors.charcoal, mb: 4 }}>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CompareIcon sx={{ color: vortxColors.gold }} />
            <Typography variant="h6" sx={{ color: vortxColors.white }}>
              Solver Verification
            </Typography>
            <Chip
              label={`1 per ${stats?.comparison_frequency || 5} runs`}
              size="small"
              sx={{ bgcolor: alpha(vortxColors.gold, 0.2), color: vortxColors.gold, ml: 1 }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Run verification now using the most recent optimization">
              <Button
                variant="outlined"
                size="small"
                onClick={handleTriggerComparison}
                disabled={triggering}
                sx={{
                  borderColor: vortxColors.gold,
                  color: vortxColors.gold,
                  '&:hover': { borderColor: vortxColors.gold, bgcolor: alpha(vortxColors.gold, 0.1) }
                }}
              >
                {triggering ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                Run Now
              </Button>
            </Tooltip>
            <IconButton onClick={fetchStats} sx={{ color: 'rgba(255,255,255,0.7)' }}>
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Status Message with Typewriter Effect */}
        {displayedMessage && (
          <Box sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 1,
            bgcolor: statusMessage.startsWith('Error') ? alpha('#e74c3c', 0.1) : alpha(vortxColors.gold, 0.1),
            border: `1px solid ${statusMessage.startsWith('Error') ? alpha('#e74c3c', 0.3) : alpha(vortxColors.gold, 0.3)}`
          }}>
            <Typography
              variant="body2"
              sx={{
                color: statusMessage.startsWith('Error') ? '#e74c3c' : vortxColors.gold,
                fontFamily: vortxTypography.mono,
                fontSize: '0.85rem'
              }}
            >
              {displayedMessage}
              <Box component="span" sx={{
                display: 'inline-block',
                width: 8,
                height: 14,
                bgcolor: 'currentColor',
                ml: 0.5,
                animation: 'blink 1s step-end infinite',
                '@keyframes blink': {
                  '50%': { opacity: 0 }
                }
              }} />
            </Typography>
          </Box>
        )}

        {/* Stats Grid */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Pass Rate */}
          <Grid item xs={12} md={4}>
            <Box sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: alpha(passRate >= 90 ? '#2ecc71' : passRate >= 70 ? '#f39c12' : '#e74c3c', 0.15),
              border: `1px solid ${alpha(passRate >= 90 ? '#2ecc71' : passRate >= 70 ? '#f39c12' : '#e74c3c', 0.3)}`
            }}>
              <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Pass Rate
              </Typography>
              <Typography variant="h3" sx={{ color: vortxColors.white, fontWeight: 700 }}>
                {passRate.toFixed(1)}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={passRate}
                sx={{
                  mt: 1,
                  height: 6,
                  borderRadius: 3,
                  bgcolor: alpha(vortxColors.stone, 0.3),
                  '& .MuiLinearProgress-bar': {
                    bgcolor: passRate >= 90 ? '#2ecc71' : passRate >= 70 ? '#f39c12' : '#e74c3c'
                  }
                }}
              />
            </Box>
          </Grid>

          {/* Total Comparisons */}
          <Grid item xs={6} md={2}>
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha(vortxColors.gold, 0.1) }}>
              <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Total
              </Typography>
              <Typography variant="h4" sx={{ color: vortxColors.white, fontWeight: 600 }}>
                {stats?.total_comparisons || 0}
              </Typography>
            </Box>
          </Grid>

          {/* Passes */}
          <Grid item xs={6} md={2}>
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#2ecc71', 0.1) }}>
              <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Passes
              </Typography>
              <Typography variant="h4" sx={{ color: '#2ecc71', fontWeight: 600 }}>
                {stats?.total_passes || 0}
              </Typography>
            </Box>
          </Grid>

          {/* Failures */}
          <Grid item xs={6} md={2}>
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#e74c3c', 0.1) }}>
              <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Failures
              </Typography>
              <Typography variant="h4" sx={{ color: '#e74c3c', fontWeight: 600 }}>
                {stats?.total_failures || 0}
              </Typography>
            </Box>
          </Grid>

          {/* Warnings/Errors */}
          <Grid item xs={6} md={2}>
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#f39c12', 0.1) }}>
              <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Warnings
              </Typography>
              <Typography variant="h4" sx={{ color: '#f39c12', fontWeight: 600 }}>
                {(stats?.total_warnings || 0) + (stats?.total_nrel_errors || 0)}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Next comparison indicator */}
        <Box sx={{ mb: 3, p: 2, bgcolor: alpha(vortxColors.stone, 0.1), borderRadius: 2 }}>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Next comparison in: <strong style={{ color: vortxColors.white }}>
              {(stats?.comparison_frequency || 5) - (stats?.vortx_runs_since_last || 0)}
            </strong> Vortx Flux runs
          </Typography>
          {stats?.last_comparison_at && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Last comparison: {new Date(stats.last_comparison_at).toLocaleString()}
            </Typography>
          )}
        </Box>

        {/* Recent Comparisons - Scrollable with expandable bars */}
        {recentComparisons.length > 0 && (
          <>
            <Divider sx={{ bgcolor: vortxColors.stone, opacity: 0.3, my: 2 }} />
            <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2 }}>
              Recent Comparisons ({recentComparisons.length})
            </Typography>
            <Box sx={{ maxHeight: 500, overflowY: 'auto', pr: 1 }}>
              {recentComparisons.map((comp) => {
                const isExpanded = expandedId === comp.id;
                const statusColor = comp.overall_status === 'pass' ? '#2ecc71' : comp.overall_status === 'fail' ? '#e74c3c' : '#f39c12';

                return (
                  <Box key={comp.id} sx={{
                    mb: 1,
                    borderRadius: 2,
                    bgcolor: alpha(vortxColors.stone, 0.05),
                    border: `1px solid ${alpha(statusColor, 0.3)}`,
                    overflow: 'hidden'
                  }}>
                    {/* Collapsed Header */}
                    <Box
                      sx={{
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: alpha(vortxColors.stone, 0.1) }
                      }}
                      onClick={() => toggleExpanded(comp.id)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                        {getStatusChip(comp.overall_status)}
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                          {comp.timestamp ? new Date(comp.timestamp).toLocaleString() : '-'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: vortxColors.gold }}>
                          PV: {comp.vortx_results?.pv_size_kw?.toFixed(0) || '-'} vs {comp.nrel_results?.pv_size_kw?.toFixed(0) || '-'} kW
                        </Typography>
                        {comp.failing_count > 0 && (
                          <Chip
                            label={`${comp.failing_count} failing`}
                            size="small"
                            sx={{
                              fontSize: '0.6rem',
                              height: 18,
                              bgcolor: alpha('#e74c3c', 0.2),
                              color: '#e74c3c'
                            }}
                          />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title="Delete this comparison">
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handleDelete(comp.id); }}
                            disabled={deleting === comp.id}
                            sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#e74c3c' } }}
                          >
                            {deleting === comp.id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        {isExpanded ? <ExpandLessIcon sx={{ color: 'rgba(255,255,255,0.5)' }} /> : <ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.5)' }} />}
                      </Box>
                    </Box>

                    {/* Expanded Content */}
                    <Collapse in={isExpanded}>
                      <Box sx={{ p: 2, pt: 0 }}>
                        {/* Key Metrics Grid */}
                        <Grid container spacing={2}>
                          <Grid item xs={6} md={3}>
                            <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: alpha(vortxColors.gold, 0.05) }}>
                              <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.65rem' }}>
                                BAU Bill ($/yr)
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                                <Box>
                                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>VF:</Typography>
                                  <Typography variant="body2" sx={{ color: vortxColors.gold, fontWeight: 600 }}>
                                    ${comp.vortx_results?.bau_bill?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>NREL:</Typography>
                                  <Typography variant="body2" sx={{ color: vortxColors.white }}>
                                    ${comp.nrel_results?.bau_bill?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: alpha('#2ecc71', 0.05) }}>
                              <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.65rem' }}>
                                Post-DER Bill ($/yr)
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                                <Box>
                                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>VF:</Typography>
                                  <Typography variant="body2" sx={{ color: '#2ecc71', fontWeight: 600 }}>
                                    ${comp.vortx_results?.year_one_bill?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>NREL:</Typography>
                                  <Typography variant="body2" sx={{ color: vortxColors.white }}>
                                    ${comp.nrel_results?.year_one_bill?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: alpha(vortxColors.stone, 0.1) }}>
                              <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.65rem' }}>
                                PV Size (kW)
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                                <Box>
                                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>VF:</Typography>
                                  <Typography variant="body2" sx={{ color: vortxColors.white }}>
                                    {comp.vortx_results?.pv_size_kw?.toFixed(1) || '-'}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>NREL:</Typography>
                                  <Typography variant="body2" sx={{ color: vortxColors.white }}>
                                    {comp.nrel_results?.pv_size_kw?.toFixed(1) || '-'}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: alpha(vortxColors.stone, 0.1) }}>
                              <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.65rem' }}>
                                Battery (kW/kWh)
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                                <Box>
                                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>VF:</Typography>
                                  <Typography variant="body2" sx={{ color: vortxColors.white }}>
                                    {comp.vortx_results?.battery_size_kw?.toFixed(1) || '-'}/{comp.vortx_results?.battery_size_kwh?.toFixed(1) || '-'}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>NREL:</Typography>
                                  <Typography variant="body2" sx={{ color: vortxColors.white }}>
                                    {comp.nrel_results?.battery_size_kw?.toFixed(1) || '-'}/{comp.nrel_results?.battery_size_kwh?.toFixed(1) || '-'}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>

                        {/* Metric Diffs */}
                        {comp.metric_diffs && comp.metric_diffs.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                              Metric Tolerance Check ({comp.failing_count || 0} failing):
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                              {comp.metric_diffs.map((diff, didx) => (
                                <Chip
                                  key={didx}
                                  label={`${diff.metric_name}: ${diff.diff_pct !== null ? diff.diff_pct.toFixed(1) + '%' : 'N/A'}`}
                                  size="small"
                                  sx={{
                                    fontSize: '0.65rem',
                                    height: 22,
                                    bgcolor: diff.within_tolerance ? alpha('#2ecc71', 0.2) : alpha('#e74c3c', 0.2),
                                    color: diff.within_tolerance ? '#2ecc71' : '#e74c3c',
                                    border: `1px solid ${diff.within_tolerance ? '#2ecc71' : '#e74c3c'}`
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}

                        {/* NREL Error */}
                        {comp.nrel_error && (
                          <Box sx={{ mt: 2, p: 1, bgcolor: alpha('#e74c3c', 0.1), borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#e74c3c' }}>
                              NREL Error: {comp.nrel_error}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}

const SolverMonitorPage = () => {
  const { currentUser } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [engineFilter, setEngineFilter] = useState('all');

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await currentUser.getIdToken();
      const params = new URLSearchParams({ limit: '100' });
      if (engineFilter !== 'all') {
        params.set('engine', engineFilter);
      }

      const response = await fetch(`${API_BASE_URL}/api/site_dna/v1/solver/jobs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch jobs: ${response.statusText}`);
      }

      const data = await response.json();
      setJobs(data.jobs || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser, engineFilter]);

  useEffect(() => {
    if (currentUser) {
      fetchJobs();
    }
  }, [currentUser, fetchJobs]);

  // Calculate stats
  const stats = {
    total: jobs.length,
    vortxFlux: jobs.filter(j => j.engine === 'vortx_flux').length,
    nrelReopt: jobs.filter(j => j.engine === 'nrel_reopt').length
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    try {
      return new Date(isoString).toLocaleString();
    } catch {
      return isoString;
    }
  };

  const StatCard = ({ title, value, icon, color }) => (
    <Card sx={{ bgcolor: alpha(color, 0.1), border: `1px solid ${alpha(color, 0.3)}` }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ color }}>{icon}</Box>
        <Box>
          <Typography variant="h4" sx={{ color, fontWeight: 700 }}>{value}</Typography>
          <Typography variant="body2" color="text.secondary">{title}</Typography>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <AdminLayout title="Solver Monitor" requireSuperAdmin>
      {/* Solver Verification Section */}
      <SolverComparisonCard />

      {/* Job Stats Cards */}
      <Typography variant="h6" sx={{ mb: 2, color: vortxColors.white }}>
        Recent Optimization Jobs
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <StatCard
            title="Total Jobs"
            value={stats.total}
            icon={<SolverIcon fontSize="large" />}
            color={vortxColors.gold}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            title="Vortx Flux"
            value={stats.vortxFlux}
            icon={<SuccessIcon fontSize="large" />}
            color={vortxColors.teal}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            title="NREL REopt"
            value={stats.nrelReopt}
            icon={<PendingIcon fontSize="large" />}
            color="#f59e0b"
          />
        </Grid>
      </Grid>

      {/* Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Engine Filter</InputLabel>
          <Select
            value={engineFilter}
            label="Engine Filter"
            onChange={(e) => setEngineFilter(e.target.value)}
          >
            <MenuItem value="all">All Engines</MenuItem>
            <MenuItem value="vortx_flux">Vortx Flux</MenuItem>
            <MenuItem value="nrel_reopt">NREL REopt</MenuItem>
          </Select>
        </FormControl>

        <Tooltip title="Refresh">
          <IconButton onClick={fetchJobs} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : jobs.length === 0 ? (
        <Alert severity="info">
          No optimization jobs found.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Run UUID</TableCell>
                <TableCell>Engine</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Project ID</TableCell>
                <TableCell>Scenario ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job) => {
                const engineInfo = ENGINE_COLORS[job.engine] || { bg: '#6b7280', label: job.engine };
                return (
                  <TableRow key={job.run_uuid} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: vortxTypography.mono, fontSize: '0.75rem' }}>
                        {job.run_uuid}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={engineInfo.label}
                        size="small"
                        sx={{
                          bgcolor: alpha(engineInfo.bg, 0.2),
                          color: engineInfo.bg,
                          fontWeight: 600
                        }}
                      />
                    </TableCell>
                    <TableCell>{formatDate(job.created_at)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: vortxTypography.mono, fontSize: '0.75rem' }}>
                        {job.project_id || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: vortxTypography.mono, fontSize: '0.75rem' }}>
                        {job.scenario_id || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </AdminLayout>
  );
};

export default SolverMonitorPage;
